import React, { useEffect, useState, useRef } from 'react';
import Maplibre from 'react-map-gl';
import { Marker, ScaleControl } from 'react-map-gl';
import { simplify } from '@turf/simplify';
import { bbox } from '@turf/bbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import mapboxgl from 'mapbox-gl';
import EventBus from '../../common/EventBus';
import { updateLayerOrder, getLineBackground } from '../../common/MapTools';
import { getFormattedTimeDiff } from "../../common/ConvertTools";

// eslint-disable-next-line import/no-webpack-loader-syntax
mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

const PerfsMap = React.memo(({
  startMarkerCoordinates,
  endMarkerCoordinates,
  contentData,
  contentDataLap1,
  contentDataLap2,
  contentRunSelected,
  followMode,
  lapsSelected,
  mapStyle,
  MAPBOX_ACCESS_TOKEN,
  mapRef,
  finishLineGeoJson,
  isDesktop,
}) => {

  // console.log("PerfsMap / redraw");

  const finishLineLayerAddedRef = useRef(false);
  const [markersCoordinates, setMarkersCoordinates] = useState({
    currentMarker1: [],
    currentMarker2: [],
  });

  const previousCoordinates = useRef(null);
  const previousBearing = useRef(0);
  const markersCoordinatesRef = useRef(markersCoordinates);


  useEffect(() => {
    markersCoordinatesRef.current = markersCoordinates;
  }, [markersCoordinates]);

  const updateCamera = () => {
    if (!followMode) return;
    if (!previousCoordinates.current)
      previousCoordinates.current = markersCoordinates.currentMarker1;

    const map = mapRef.current.getMap();
    const offsetDistance = 0.000001;

    const currentMarker = markersCoordinatesRef.current.currentMarker1;

    if (currentMarker.length > 0) {
      const deltaLng = currentMarker[0] - previousCoordinates.current[0];
      const deltaLat = currentMarker[1] - previousCoordinates.current[1];

      let bearing = previousBearing.current;

      if (Math.abs(deltaLng) > 1e-6 || Math.abs(deltaLat) > 1e-6) {
        // Calculate new bearing
        bearing = (Math.atan2(deltaLng, deltaLat) * 180) / Math.PI;
        bearing = (bearing + 360) % 360;

        // Smooth out the bearing transition
        if (previousBearing.current !== null) {
          let bearingDiff = bearing - previousBearing.current;
          if (bearingDiff > 180) bearingDiff -= 360;
          else if (bearingDiff < -180) bearingDiff += 360;

          bearing = previousBearing.current + bearingDiff;
        }
      }

      // Update the camera's position behind the user
      const radians = (bearing * Math.PI) / 180;
      const cameraLng = currentMarker[0] - offsetDistance * Math.sin(radians);
      const cameraLat = currentMarker[1] - offsetDistance * Math.cos(radians);

      // Move the camera smoothly to the new position and bearing
      map.easeTo({
        center: [cameraLng, cameraLat],
        bearing: bearing,
        pitch: 80,
        zoom: 22,
        duration: 200,
        easing: (t) => t,
      });

      // Update the previous coordinates and bearing
      previousCoordinates.current = [currentMarker[0], currentMarker[1]];
      previousBearing.current = bearing;
    }
  };

  useEffect(() => {
    if (followMode) {
      const intervalId = setInterval(updateCamera, 200);
      return () => clearInterval(intervalId);
    }
  }, [followMode]);

  useEffect(() => {
    const handleUpdateCurrentMarkersCoordinates = (coordinates) => {
      setMarkersCoordinates({
        currentMarker1: coordinates[0],
        currentMarker2: coordinates[1],
      });

      markersCoordinatesRef.current = {
        currentMarker1: coordinates[0],
        currentMarker2: coordinates[1],
      };

      if (coordinates[0].length > 0) {
        //  previousCoordinates.current = coordinates[0];
      }
    };

    EventBus.on('updateCurrentMarkersCoordinates', handleUpdateCurrentMarkersCoordinates);

    return () => {
      EventBus.remove('updateCurrentMarkersCoordinates', handleUpdateCurrentMarkersCoordinates);
    };
  }, []);


  const speedMax = contentRunSelected.maxSpeed;


  useEffect(() => {
    if (mapRef.current && contentData.length > 0) {
      let map = mapRef.current.getMap();

      const firstItemIndex = 0;
      /*
      const firstItemIndex = contentData.findIndex(
        (item) => item.positionGps_longitude !== 0 && item.positionGps_latitude !== 0
      );

      if (firstItemIndex === -1) return;
      */

      let mapDataRoute1, mapDataRoute2;

      if ((lapsSelected && lapsSelected.length === 1) && (contentDataLap1) && (contentDataLap1.length > 0)) {
        mapDataRoute1 = contentDataLap1
          .slice(firstItemIndex)
          .map((item) => [item.positionGps_longitude, item.positionGps_latitude]);
      } else if ((lapsSelected && lapsSelected.length === 2) && (contentDataLap1) && (contentDataLap2) && (contentDataLap1.length > 0) && (contentDataLap2.length > 0)) {
        mapDataRoute1 = contentDataLap1
          .slice(firstItemIndex)
          .map((item) => [item.positionGps_longitude, item.positionGps_latitude]);
        mapDataRoute2 = contentDataLap2
          .slice(firstItemIndex)
          .map((item) => [item.positionGps_longitude, item.positionGps_latitude]);
      } else {
        mapDataRoute1 = contentData
          .slice(firstItemIndex)
          .map((item) => [item.positionGps_longitude, item.positionGps_latitude]);
      }


      const geojsonRoute = {
        'type': 'FeatureCollection',
        'features': [{
          'type': 'Feature',
          'geometry': {
            'type': 'LineString',
            'coordinates': mapDataRoute1
          }
        }]
      };

      const geojsonRoute2 = {
        'type': 'FeatureCollection',
        'features': [{
          'type': 'Feature',
          'geometry': {
            'type': 'LineString',
            'coordinates': mapDataRoute2
          }
        }]
      };

      try {
        if (geojsonRoute.features[0].geometry.data) {

          const newGeojsonRoute = simplify(geojsonRoute.features[0].geometry.data, { tolerance: 0.0000001, highQuality: true });
          // console.log('original geojsonRoute', geojsonRoute.data.geometry.coordinates.length, 'newGeojsonRoute', newGeojsonRoute.geometry.coordinates.length);
          geojsonRoute.features[0].geometry.data = newGeojsonRoute.geometry;
        }
      } catch (error) {
        console.error(error);
      }

      const addRouteLayer = () => {
        if (map.getLayer('route')) {
          map.removeLayer('route');
        }
        if (map.getSource('route')) {
          map.removeSource('route');
        }
        if (map.getLayer('route2')) {
          map.removeLayer('route2');
        }
        if (map.getSource('route2')) {
          map.removeSource('route2');
        }

        map.addSource('route', {
          type: 'geojson',
          lineMetrics: true,
          data: geojsonRoute
        });


        map.addLayer({
          id: 'route',
          type: 'line',
          source: 'route',
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
          paint: {
            'line-color': '#00f',
            'line-width': 5,
            'line-gradient': [
              'interpolate',
              ['linear'],
              ['line-progress'],

              ...getLineBackground((contentDataLap1 && contentDataLap1.length > 0) ? contentDataLap1 : contentData, speedMax)
            ]

          },
        });


        if (mapDataRoute2 && mapDataRoute2.length > 0) {
          map.addSource('route2', {
            type: 'geojson',
            lineMetrics: true,
            data: geojsonRoute2
          });


          map.addLayer({
            id: 'route2',
            type: 'line',
            source: 'route2',
            layout: {
              'line-join': 'round',
              'line-cap': 'round',
            },
            paint: {
              'line-color': '#00f',
              'line-width': 5,
              'line-gradient': [
                'interpolate',
                ['linear'],
                ['line-progress'],

                ...getLineBackground(contentDataLap2, speedMax)
              ]

            },
          });
        }



        var bboxBounds = bbox(geojsonRoute.features[0].geometry);
        const bounds2 = [
          {
            lat: bboxBounds[1],
            lon: bboxBounds[0],
          },
          {
            lat: bboxBounds[3],
            lon: bboxBounds[2],
          },
        ];

        map.fitBounds(bounds2, {
          padding: 50,
        });

        // Add daytime fog
        map.setFog({
          'range': [10, 20],
          'color': 'white',
          /*
          'range': [1000, 10000],            // Increase the range to push the fog further away
          'horizon-blend': 0.001,          // Reduce the blend with the horizon for less intense fog near the camera
          'high-color': '#add8e6',       // Fog color at the top of the atmosphere (further away)
          'space-color': '#d8f2ff',      // Color of the space (sky) beyond the atmosphere
          'star-intensity': 0.0          // Star visibility in the sky (set to 0 for daytime)
          */
        });


        // Update the layer order to ensure finishLine is on top
        if (map.getLayer('finishLine')) {
          updateLayerOrder(map, 'route', 'route2');
          updateLayerOrder(map, 'route2', 'finishLine');
        }
      };

      map.once('idle', () => {
        console.log("PerfsMap / useEffect2 / idle");
        addRouteLayer();
      });

      addRouteLayer();
    }

    // Return a cleanup function to remove the event listener
    return () => {
    };
  }, [contentData, contentDataLap1, contentDataLap2, mapStyle, contentRunSelected, lapsSelected]);

  useEffect(() => {
    if (mapRef.current) {
      const map = mapRef.current.getMap();

      const addFinishLineLayer = () => {
        finishLineLayerAddedRef.current = true;
        console.log("PerfsMap / addFinishLineLayer / finishLineGeoJson", finishLineGeoJson);

        if (map.getLayer('finishLine')) {
          map.removeLayer('finishLine');
        }
        if (map.getSource('finishLine')) {
          map.removeSource('finishLine');
        }

        if (finishLineGeoJson) {
          map.addSource('finishLine', {
            type: 'geojson',
            data: finishLineGeoJson,
          });

          map.addLayer({
            id: 'finishLine',
            type: 'line',
            source: 'finishLine',
            paint: {
              'line-color': 'red',
              'line-width': 3,
            },
          });
        }

        // Update the layer order to ensure finishLine is on top
        updateLayerOrder(map, 'route', 'route2');
        updateLayerOrder(map, 'route2', 'finishLine');

      };

      map.once('idle', () => {
        console.log("PerfsMap / useEffect2 / idle");
        // cleanLayers();
        addFinishLineLayer(true);
      });
    }

    // Return a cleanup function to remove the event listener
    return () => {

      if (mapRef.current) {
        const map = mapRef.current.getMap();
        // map.off('idle');
      }
    };

  }, [finishLineGeoJson, contentData, mapStyle]);



  return (
    <Maplibre
      initialViewState={{
        latitude: 48,
        longitude: 1,
        zoom: 4,
        container: 'map-container',
        attributionControl: false,
      }}
      style={{ width: '100%', height: '100%' }}
      mapStyle={mapStyle}
      mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
      ref={mapRef}
      container='map'
      cooperativeGestures={isDesktop ? false : true}
    >
      <ScaleControl />

      {startMarkerCoordinates.length > 0 && (
        <Marker longitude={startMarkerCoordinates[0]} latitude={startMarkerCoordinates[1]} anchor='bottom'>
          <i className='fa fa-map-marker-alt marker-icon' style={{ color: '#86cf00' }}></i>
        </Marker>
      )}

      {endMarkerCoordinates.length > 0 && (
        <Marker longitude={endMarkerCoordinates[0]} latitude={endMarkerCoordinates[1]} anchor='bottom'>
          <i className='fa fa-map-marker-alt marker-icon' style={{ color: '#000' }}></i>
        </Marker>
      )}

      {markersCoordinates.currentMarker1.length > 0 && (
        <Marker longitude={markersCoordinates.currentMarker1[0]} latitude={markersCoordinates.currentMarker1[1]} anchor='center'>
          <svg height="20" width="20">
            <circle cx="10" cy="10" r="9" fill="red" stroke="white" strokeWidth="2" />
          </svg>

          {((markersCoordinates.currentMarker2.length > 2) && (lapsSelected.length > 1)) && (
            <div
              className='perf-sticker'
              style={{
                position: 'absolute',
                top: '-35px', // Adjust as necessary
                right: '-70px', // Adjust as necessary to align it with the marker
                backgroundColor: '#343a40', // Dark grey background for the sticker
                color: 'white', // Text color for the name
                fontWeight: 'bold',
                fontSize: '20px', // Adjust the font size as necessary
                padding: '4px 8px', // Padding inside the sticker
                display: 'flex',
                alignItems: 'center', // Align items vertically in the center
                borderRadius: '6px', // Rounded corners for the sticker
                boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.5)', // Shadow for depth
                whiteSpace: 'nowrap',
              }}
            >
              {getFormattedTimeDiff(markersCoordinates.currentMarker2)}
            </div>
          )}
        </Marker>
      )}

      {markersCoordinates.currentMarker2.length > 0 && (
        <Marker longitude={markersCoordinates.currentMarker2[0]} latitude={markersCoordinates.currentMarker2[1]} anchor='center'>
          <svg height="20" width="20">
            <circle cx="10" cy="10" r="9" fill="rgba(255, 0, 0, 0.5)" stroke="white" strokeWidth="1" strokeDasharray="4,2" />
          </svg>

        </Marker>
      )}
    </Maplibre>
  );
})

export default React.memo(PerfsMap);
